import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { FaEnvelope, FaTimes, FaHeart, FaRegHeart, FaSpinner, FaChevronRight, FaChevronLeft } from 'react-icons/fa';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Bandeja de entrada unificada de Mensajes de la Coach.
 * Muestra:
 *   - Mensajes broadcast dirigidos al email de la usuaria (individuales).
 *   - Mensajes broadcast al plan/categoría (Fit Evolution / Asesoría 1 a 1 …).
 *   - Mensaje agendado del día actual (según semana/día del proceso).
 * Marca de "no leído" y contador en la burbuja del sobre.
 */
export default function CoachMessagesInbox() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [unread, setUnread] = useState(0);

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('deisycfit_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  // Poll ligero para el contador (cada 60s) — así el globo se enciende sin recargar.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/coach-messages/unread-count`, { headers: authHeaders() });
        if (!cancelled) setUnread(data?.unread_count || 0);
      } catch (e) { /* silencioso */ }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [authHeaders]);

  const loadInbox = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/coach-messages/inbox`, { headers: authHeaders() });
      setItems(data?.items || []);
      setUnread(data?.unread_count || 0);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const openModal = () => {
    setOpen(true);
    setSelected(null);
    loadInbox();
  };

  const openMessage = async (msg) => {
    setSelected(msg);
    if (!msg.read) {
      try {
        await axios.post(`${API}/coach-messages/${msg.id}/read`, {}, { headers: authHeaders() });
        setItems((prev) => prev.map((m) => m.id === msg.id ? { ...m, read: true } : m));
        setUnread((u) => Math.max(0, u - 1));
      } catch (e) { /* noop */ }
    }
  };

  const toggleFavorite = async (msg) => {
    try {
      const { data } = await axios.post(`${API}/coach-messages/${msg.id}/favorite`, {}, { headers: authHeaders() });
      if (data.favorited) {
        setItems((prev) => prev.map((m) => m.id === msg.id ? { ...m, favorited: true } : m));
        if (selected?.id === msg.id) setSelected({ ...selected, favorited: true });
      } else {
        // Al quitarlo de favoritos: sale de la bandeja (que solo muestra favoritos)
        setItems((prev) => prev.filter((m) => m.id !== msg.id));
        setSelected(null);
      }
    } catch (e) { /* noop */ }
  };

  const badgeFor = (msg) => {
    if (msg.send_mode === 'broadcast') {
      if ((msg.target_emails || []).length > 0) return { label: 'Para ti', color: 'bg-emerald-500/20 text-emerald-300 border-emerald-400/40' };
      // Mensaje grupal: la clienta NUNCA debe ver el detalle interno del envío
      // (ej. "Todas las clientas", "Fit Evolution", "Asesoría 1 a 1"…). Ese es
      // dato del admin. Etiqueta genérica "Coach 💬".
      return { label: 'Coach 💬', color: 'bg-cyan-500/20 text-cyan-300 border-cyan-400/40' };
    }
    return { label: `Semana ${msg.week_number} · Día ${msg.day_in_week}`, color: 'bg-pink-500/20 text-pink-300 border-pink-400/40' };
  };

  return (
    <>
      {/* Botón flotante inferior derecha */}
      <button
        onClick={openModal}
        data-testid="coach-inbox-fab"
        className="fixed bottom-24 right-6 z-50 w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all hover:scale-105 active:scale-95"
        style={{
          background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))',
          boxShadow: '0 8px 24px rgba(var(--brand-primary-glow), 0.45)',
        }}
        aria-label="Ver mensajes de tu coach"
        title="Mensajes de tu coach"
      >
        <FaEnvelope className="text-white text-lg" />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1.5 rounded-full text-[11px] font-bold flex items-center justify-center text-white shadow-lg ring-2 ring-white/70 animate-pulse"
            style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)' }}
            data-testid="coach-inbox-unread"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[170] flex items-end sm:items-center justify-center p-0 sm:p-4"
            style={{ background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(6px)' }}
            onClick={() => setOpen(false)}
          >
            <motion.div
              initial={{ y: 60, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 60, opacity: 0, scale: 0.98 }}
              transition={{ type: 'spring', damping: 24 }}
              className="w-full sm:max-w-lg h-[85vh] sm:h-[75vh] rounded-t-3xl sm:rounded-3xl overflow-hidden flex flex-col"
              style={{
                background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)',
                border: '1px solid rgba(var(--brand-primary-glow), 0.25)',
              }}
              onClick={(e) => e.stopPropagation()}
              data-testid="coach-inbox-modal"
            >
              {/* Header */}
              <div className="p-4 border-b border-white/10 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-2">
                  {selected && (
                    <button
                      onClick={() => setSelected(null)}
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white hover:bg-white/10"
                      aria-label="Volver"
                    >
                      <FaChevronLeft />
                    </button>
                  )}
                  <div>
                    <h3 className="text-white font-bold flex items-center gap-2">
                      💌 {selected ? 'Mensaje guardado' : 'Mis Guardados'}
                    </h3>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      {selected
                        ? (badgeFor(selected).label)
                        : `${items.length} mensaje${items.length === 1 ? '' : 's'}`}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white hover:bg-white/10"
                  data-testid="coach-inbox-close"
                >
                  <FaTimes />
                </button>
              </div>

              {/* Contenido */}
              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="text-center py-16 text-slate-400">
                    <FaSpinner className="animate-spin inline text-2xl" />
                  </div>
                ) : items.length === 0 ? (
                  <div className="text-center py-16 px-6 text-slate-500">
                    <span className="text-5xl">📭</span>
                    <p className="text-sm mt-3">Aún no has guardado ningún mensaje</p>
                    <p className="text-xs mt-1 text-slate-600">
                      Cuando llegue un aviso de tu coach, presiona <span className="text-pink-300 font-semibold">Guardar</span> para conservarlo aquí.
                    </p>
                  </div>
                ) : selected ? (
                  // ── Vista detallada de un mensaje ──
                  <div className="p-5" data-testid="coach-inbox-detail">
                    <div className="text-center mb-4">
                      <div
                        className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-3"
                        style={{ background: 'rgba(var(--brand-primary-glow), 0.15)' }}
                      >
                        <span className="text-3xl">{selected.icon || '💪'}</span>
                      </div>
                      <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${badgeFor(selected).color} mb-2`}>
                        {badgeFor(selected).label}
                      </span>
                      <h4 className="text-lg font-bold text-white">{selected.title}</h4>
                    </div>
                    <div
                      className="rounded-2xl p-4 mb-4 text-left"
                      style={{
                        background: 'rgba(255, 255, 255, 0.03)',
                        border: '1px solid rgba(255, 255, 255, 0.06)',
                      }}
                    >
                      <div className="text-slate-200 text-[15px] leading-relaxed" data-testid="coach-inbox-body-md">
                        <ReactMarkdown
                          components={{
                            p: ({ children }) => <p className="my-2 text-slate-100/95">{children}</p>,
                            strong: ({ children }) => <strong className="text-pink-300 font-bold">{children}</strong>,
                            em: ({ children }) => <em className="text-cyan-300/90 italic">{children}</em>,
                            h1: ({ children }) => <h4 className="text-lg font-black text-white mt-3 mb-1.5">{children}</h4>,
                            h2: ({ children }) => <h4 className="text-base font-black text-white mt-3 mb-1.5">{children}</h4>,
                            h3: ({ children }) => <h4 className="text-base font-bold text-pink-200 mt-2 mb-1">{children}</h4>,
                            ul: ({ children }) => <ul className="list-disc list-inside space-y-1 my-2 text-slate-100/95">{children}</ul>,
                            ol: ({ children }) => <ol className="list-decimal list-inside space-y-1 my-2 text-slate-100/95">{children}</ol>,
                            li: ({ children }) => <li className="text-slate-100/95">{children}</li>,
                            a: ({ href, children }) => (
                              <a href={href} target="_blank" rel="noopener noreferrer" className="text-cyan-300 underline">
                                {children}
                              </a>
                            ),
                            hr: () => <hr className="my-3 border-white/10" />,
                          }}
                        >
                          {selected.body || ''}
                        </ReactMarkdown>
                      </div>
                    </div>
                    <p className="text-right text-xs text-pink-300/70 italic mb-5">
                      — Deisy Carolina
                    </p>
                    <button
                      onClick={() => toggleFavorite(selected)}
                      className={`w-full py-2.5 rounded-full text-sm border flex items-center justify-center gap-2 transition-colors ${
                        selected.favorited
                          ? 'text-white border-transparent'
                          : 'text-pink-300 border-pink-500/40 hover:bg-pink-500/10'
                      }`}
                      style={selected.favorited ? { background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' } : {}}
                      data-testid="coach-inbox-toggle-favorite"
                    >
                      {selected.favorited ? <><FaHeart /> Guardado</> : <><FaRegHeart /> Guardar</>}
                    </button>
                  </div>
                ) : (
                  // ── Lista tipo bandeja de entrada ──
                  <ul className="divide-y divide-white/5" data-testid="coach-inbox-list">
                    {items.map((msg) => {
                      const b = badgeFor(msg);
                      return (
                        <li key={msg.id}>
                          <button
                            onClick={() => openMessage(msg)}
                            className={`w-full p-4 flex items-start gap-3 text-left hover:bg-white/5 transition-colors ${!msg.read ? 'bg-white/[0.02]' : ''}`}
                            data-testid={`coach-inbox-item-${msg.id}`}
                          >
                            <div
                              className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 relative"
                              style={{ background: 'rgba(var(--brand-primary-glow), 0.15)' }}
                            >
                              <span className="text-xl">{msg.icon || '💪'}</span>
                              {!msg.read && (
                                <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-pink-500 ring-2 ring-[#16213e]" />
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-baseline gap-2">
                                <h4 className={`text-sm truncate ${!msg.read ? 'text-white font-bold' : 'text-slate-200 font-semibold'}`}>
                                  {msg.title}
                                </h4>
                              </div>
                              <p className="text-slate-400 text-xs line-clamp-2 mt-0.5">{msg.body}</p>
                              <div className="flex items-center gap-2 mt-1.5">
                                <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${b.color}`}>
                                  {b.label}
                                </span>
                                {msg.favorited && <FaHeart className="text-pink-400 text-[10px]" />}
                              </div>
                            </div>
                            <FaChevronRight className="text-slate-500 text-xs mt-3 flex-shrink-0" />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
